import type { McpServer, Skill } from "@dayvise/domain";
import { Badge, Button, Card, MUTED_TEXT, Spinner, Switch, rowStyle, stackStyle } from "@dayvise/react-ui";
import { useMcpServerMutations, useMcpServers, useSkillMutations, useSkills } from "./hooks.ts";
import { SkillSharingPanel } from "./SkillSharingPanel.tsx";

/**
 * MCP servers and skills, each with an explicit on/off switch.
 *
 * "Off" is enforced by the platform, not by hiding a row here: a disabled server is
 * no longer offered to a run, and a disabled skill leaves selection. Reloading only
 * re-reads the catalogue — it never edits configuration, and a stdio server's
 * executable belongs to the runtime operator.
 */
export function McpServersPanel() {
  const query = useMcpServers();
  const mutations = useMcpServerMutations();
  const error = query.error ?? mutations.setEnabled.error ?? mutations.remove.error;
  const servers: readonly McpServer[] = query.data?.servers ?? [];
  const live = servers.filter((server) => server.enabled);
  return (
    <Card
      title="MCP servers"
      subtitle="A server's tools are offered to a run as mcp__<server>__<tool>. Turning a server off keeps its configuration and credential but stops offering its tools."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <Button size="sm" variant="ghost" loading={query.isFetching} onClick={() => void query.refetch()}>Reload catalogue</Button>
          <span style={MUTED_TEXT}>
            {query.isPending ? "Reading servers" : `${live.length} of ${servers.length} enabled`}
          </span>
        </div>
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          Reloading re-reads this workspace's servers from the server. It never changes configuration, and a stdio server's executable is not editable here.
        </p>
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not read or update MCP servers"}</p> : null}
        {query.isPending ? <Spinner label="Loading MCP servers" /> : null}
        {!query.isPending && servers.length === 0 ? <p style={{ ...MUTED_TEXT, margin: 0 }}>No MCP servers are configured for this workspace.</p> : null}
        {servers.map((server) => (
          <Card
            key={server.mcpServerId}
            title={server.name}
            subtitle={server.transport === "stdio" ? "stdio transport" : server.url ?? "http transport"}
          >
            <div style={stackStyle("var(--dayvise-space-xs)")}>
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                {server.shared ? "Shared with the workspace" : "Personal to its owner"} · {server.authType} authentication ·{" "}
                {server.tools === null ? "tools not discovered yet" : `${server.tools.length} tool(s) discovered`}
              </p>
              {server.transport === "stdio" ? (
                <p style={{ ...MUTED_TEXT, margin: 0 }}>
                  This stdio server's executable comes from the runtime operator's environment. This page can neither start nor edit it.
                </p>
              ) : null}
              <Switch
                label={server.enabled ? "Enabled" : "Disabled"}
                description={server.enabled
                  ? "Its tools are offered to new runs."
                  : "Its tools are not offered to new runs; the server and its credential stay configured."}
                checked={server.enabled}
                disabled={mutations.setEnabled.isPending}
                onChange={(checked) => mutations.setEnabled.mutate({ serverId: server.mcpServerId, enabled: checked })}
              />
              <div style={rowStyle("var(--dayvise-space-xs)")}>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={mutations.test.isPending}
                  onClick={() => mutations.test.mutate(server.mcpServerId)}
                >
                  Test connection
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={mutations.remove.isPending}
                  onClick={() => {
                    if (confirm(`Remove the MCP server "${server.name}"? Its tools stop being offered immediately.`)) mutations.remove.mutate(server.mcpServerId);
                  }}
                >
                  Remove
                </Button>
              </div>
              {mutations.test.data && mutations.test.variables === server.mcpServerId ? (
                <p style={{ ...MUTED_TEXT, margin: 0 }}>{mutations.test.data.ok ? `Reachable; ${mutations.test.data.tools.length} tool(s) answered.` : "The server did not answer."}</p>
              ) : null}
            </div>
          </Card>
        ))}
      </div>
    </Card>
  );
}

export function SkillsPanel() {
  const query = useSkills(true);
  const mutations = useSkillMutations();
  const error = query.error ?? mutations.remove.error ?? mutations.restore.error;
  const skills: readonly Skill[] = query.data?.skills ?? [];
  const enabled = (skill: Skill) => skill.archivedAt === undefined || skill.archivedAt === null;
  return (
    <Card
      title="Skills"
      subtitle="A skill is a reusable instruction document. Disabling one leaves it in the library and its history but stops it being selected for a new run."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        <div style={rowStyle("var(--dayvise-space-xs)")}>
          <Button size="sm" variant="ghost" loading={query.isFetching} onClick={() => void query.refetch()}>Reload skills</Button>
          <span style={MUTED_TEXT}>
            {query.isPending ? "Reading skills" : `${skills.filter(enabled).length} of ${skills.length} enabled`}
          </span>
        </div>
        <p style={{ ...MUTED_TEXT, margin: 0 }}>
          Reloading re-reads the catalogue, including disabled skills, so one can be turned back on here.
        </p>
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not read or update skills"}</p> : null}
        {query.isPending ? <Spinner label="Loading skills" /> : null}
        {!query.isPending && skills.length === 0 ? <p style={{ ...MUTED_TEXT, margin: 0 }}>No skills are in this workspace library.</p> : null}
        {skills.map((skill) => (
          <div key={skill.skillId} style={{ ...stackStyle("var(--dayvise-space-xs)"), borderTop: "1px solid var(--dayvise-color-border)", paddingTop: "var(--dayvise-space-sm)" }}>
            <div style={rowStyle("var(--dayvise-space-xs)")}>
              <strong>{skill.name}</strong>
              <Badge tone="neutral">{skill.visibility}</Badge>
              {enabled(skill) ? null : <Badge tone="warning">Disabled</Badge>}
            </div>
            {skill.tags.length > 0 ? <span style={MUTED_TEXT}>{skill.tags.join(", ")}</span> : null}
            <Switch
              label={enabled(skill) ? "Enabled" : "Disabled"}
              description={enabled(skill)
                ? "Offered when a run selects skills."
                : "Kept in the library but not offered to a new run."}
              checked={enabled(skill)}
              disabled={mutations.remove.isPending || mutations.restore.isPending}
              onChange={(checked) => {
                if (checked) mutations.restore.mutate(skill.skillId);
                else mutations.remove.mutate(skill.skillId);
              }}
            />
          </div>
        ))}
        <SkillSharingPanel skills={skills} />
      </div>
    </Card>
  );
}
